/**
 * Calculate completion stats for a user based on their FHIR profile.
 * Returns { count, total, percent, completed: { f1, f2, f3, f4, f5 } }
 *
 * Reads from the FHIR-formatted profile object (users/{uid}.profile).
 * Privacy-aware: only checks if forms are completed, no clinical values.
 */
export function calcCompletion(profile) {
  if (!profile) {
    return { count: 0, total: 5, percent: 0, completed: { f1: false, f2: false, f3: false, f4: false, f5: false } };
  }

  const isFhir = profile.resourceType === "Patient";

  let f1 = false, f2 = false, f3 = false, f4 = false, f5 = false;

  if (isFhir) {
    f1 = !!profile.completedAt;
    const other = Array.isArray(profile.other) ? profile.other : [];
    f2 = other.some((q) => q.questionnaire === "F2-PAR-Q-Physical" && q.data);
    f3 = other.some((q) => q.questionnaire === "F3-IPAQ" && q.data);
    f4 = other.some((q) => q.questionnaire === "F4-Nutrition" && q.data);
    f5 = other.some((q) => q.questionnaire === "F5-Mental-Health" && q.data);
  } else {
    // Legacy format
    f1 = !!profile.completedAt;
    f2 = !!profile.f2Results?.completedAt;
    f3 = !!profile.f3Results?.completedAt;
    f4 = !!profile.f4Results?.completedAt;
    f5 = !!profile.f5Results?.completedAt;
  }

  const completed = { f1, f2, f3, f4, f5 };
  const count = [f1, f2, f3, f4, f5].filter(Boolean).length;
  return { count, total: 5, percent: Math.round((count / 5) * 100), completed };
}

/**
 * Build a CSV string from rows + columns.
 * Escapes values containing commas, quotes, or newlines.
 */
export function buildCsv(headers, rows) {
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.map(escape).join(",")];
  rows.forEach((row) => {
    lines.push(row.map(escape).join(","));
  });
  return lines.join("\n");
}

/**
 * Trigger CSV download in browser.
 */
export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Validate email is in allowed corporate domains list.
 * Empty/missing list = DENY ALL (privacy by default).
 * Clientes deben tener al menos un dominio corporativo configurado.
 */
export function isEmailDomainAllowed(email, domains) {
  if (!Array.isArray(domains) || domains.length === 0) return false;
  if (!email) return false;
  const lc = email.toLowerCase().trim();
  return domains.some((d) => {
    const clean = d.toLowerCase().trim().replace(/^@/, "");
    return lc.endsWith(`@${clean}`);
  });
}

/**
 * Calcula métricas poblacionales agregadas y anonimizadas para el Dashboard de RRHH (B2B).
 * Cumple con estándares de confidencialidad médica (no expone nombres con patologías).
 */
export function calcPopulationMetrics(employees = [], forceCohort = false) {
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter((e) => e.isActive !== false).length;

  // Empleados que han completado al menos el formulario F1 o tienen signos vitales
  const employeesWithData = employees.filter((e) => {
    const p = e.profile;
    return p && (p.completedAt || p.vitals || p.peso || p.paSistolica);
  });

  const participatingCount = employeesWithData.length;
  const participationRate = totalEmployees > 0 ? Math.round((participatingCount / totalEmployees) * 100) : 0;

  // Solo se activa simulación si se solicita explícitamente como demo/proyección
  const useBenchmark = !!forceCohort;

  if (useBenchmark && totalEmployees === 0) {
    // Benchmark de referencia epidemiológica corporativa para vistas vacías de preventa
    return getBenchmarkCohortMetrics(100);
  }

  // Contadores de triaje
  let l0 = 0, l1 = 0, l2 = 0, l3 = 0;
  let totalWhtr = 0, countWhtr = 0, elevatedWhtr = 0;
  let totalImc = 0, countImc = 0, normoImc = 0, sobreImc = 0, obesoImc = 0;
  let totalPas = 0, totalPad = 0, countBp = 0, normalBp = 0, elevatedBp = 0, htaBp = 0;
  let countSleep = 0, totalSleep = 0, deficitSleep = 0;
  let countStress = 0, totalStress = 0, highStress = 0, modStress = 0, lowStress = 0;
  let sedentaryCount = 0, modActivityCount = 0, vigActivityCount = 0, countActivity = 0;

  // Nuevos contadores clínicos: Glicemia, Dislipidemia, Esfera Emocional (4 pilares) y Concurrencia
  let countGlu = 0, riskGluCount = 0;
  let countLipids = 0, riskLipidCount = 0;
  let anxietyCount = 0, highAnxietyCount = 0;
  let burnoutCount = 0, highBurnoutCount = 0;
  let exhaustionCount = 0, highExhaustionCount = 0;
  let depersonalizationCount = 0, highDepersonalizationCount = 0;
  let accomplishmentCount = 0, lowAccomplishmentCount = 0;
  let wellbeingCount = 0, positiveWellbeingCount = 0;
  let countCompletedAll5 = 0;
  let countFaceScan = 0;

  // Concurrencia de factores de riesgo clínicos (0 a 4+)
  let c0 = 0, c1 = 0, c2 = 0, c3 = 0, c4Plus = 0;

  const effectiveTotalEmployees = useBenchmark ? Math.max(totalEmployees, 35) : totalEmployees;
  const dataset = useBenchmark ? generateSimulatedCohort(effectiveTotalEmployees) : employeesWithData;

  dataset.forEach((emp) => {
    const p = emp.profile || {};
    let employeeMaxLevel = 0;
    let employeeRiskFactors = 0;

    // A. Verificación de completitud de fuentes basales (F1 a F5) y FaceScan
    const completion = calcCompletion(p);
    if (completion.percent === 100) countCompletedAll5++;
    if (p.vitals?.stressIndex || p.vitals?.sbp) countFaceScan++;

    // 1. Somatometría & IMC (OMS)
    let isImcRisk = false;
    const peso = parseFloat(p.peso || p.clinical_measurements?.peso);
    const talla = parseFloat(p.talla || p.clinical_measurements?.talla);
    if (!isNaN(peso) && !isNaN(talla) && talla > 0) {
      const imc = peso / Math.pow(talla / 100, 2);
      totalImc += imc;
      countImc++;
      if (imc < 18.5) {
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
      } else if (imc < 25) {
        normoImc++;
      } else if (imc < 30) {
        sobreImc++;
        isImcRisk = true;
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
      } else if (imc < 40) {
        obesoImc++;
        isImcRisk = true;
        if (employeeMaxLevel < 2) employeeMaxLevel = 2;
      } else {
        obesoImc++;
        isImcRisk = true;
        employeeMaxLevel = 3;
      }
    }

    // 2. Relación Cintura-Altura (WHtR) - IDF / Ashwell
    let isWhtrRisk = false;
    const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
    if (!isNaN(cintura) && !isNaN(talla) && talla > 0) {
      const whtr = cintura / talla;
      totalWhtr += whtr;
      countWhtr++;
      if (whtr > 0.5) {
        elevatedWhtr++;
        isWhtrRisk = true;
        if (employeeMaxLevel < 2) employeeMaxLevel = 2; // WHtR > 0.5 dispara Nivel 2
      }
    }
    // Factor de riesgo 3: Composición corporal / Adiposidad
    if (isWhtrRisk || isImcRisk) employeeRiskFactors++;

    // 3. Presión Arterial (ACC/AHA 2017)
    let isBpRisk = false;
    const pas = parseFloat(p.paSistolica || p.vitals?.sbp);
    const pad = parseFloat(p.paDiastolica || p.vitals?.dbp);
    if (!isNaN(pas) && !isNaN(pad)) {
      totalPas += pas;
      totalPad += pad;
      countBp++;
      if (pas >= 180 || pad >= 120) {
        employeeMaxLevel = 3;
        htaBp++;
        isBpRisk = true;
      } else if (pas >= 140 || pad >= 90) {
        if (employeeMaxLevel < 2) employeeMaxLevel = 2;
        htaBp++;
        isBpRisk = true;
      } else if (pas >= 130 || pad >= 80) {
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
        htaBp++;
        isBpRisk = true;
      } else if (pas >= 120) {
        elevatedBp++;
      } else {
        normalBp++;
      }
    }
    // Factor de riesgo 1: Hemodinámica / Tensión arterial
    if (isBpRisk) employeeRiskFactors++;

    // 4. Glucemia & Diabetes (ADA 2024)
    let isGluRisk = false;
    const glu = parseFloat(p.glucemia);
    if (!isNaN(glu)) {
      countGlu++;
      if (glu > 250 || glu < 54) {
        employeeMaxLevel = 3;
        riskGluCount++;
        isGluRisk = true;
      } else if (glu >= 126 || glu < 70) {
        if (employeeMaxLevel < 2) employeeMaxLevel = 2;
        riskGluCount++;
        isGluRisk = true;
      } else if (glu >= 100) {
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
        riskGluCount++;
        isGluRisk = true;
      }
    }
    // Factor de riesgo 2: Glicemia / Resistencia a la Insulina
    if (isGluRisk) employeeRiskFactors++;

    // 5. Perfil Lipídico / Dislipidemia (NCEP ATP III)
    let isLipidRisk = false;
    const col = parseFloat(p.colesterol);
    const tg = parseFloat(p.trigliceridos);
    if (!isNaN(col) || !isNaN(tg)) {
      countLipids++;
      if ((!isNaN(col) && col >= 200) || (!isNaN(tg) && tg >= 150)) {
        riskLipidCount++;
        isLipidRisk = true;
        if (employeeMaxLevel < 2) employeeMaxLevel = 2;
      }
    }
    // Factor de riesgo 4: Dislipidemia
    if (isLipidRisk) employeeRiskFactors++;

    // 6. Hábitos de Sueño (NSF 2015)
    const sueno = parseFloat(p.sueno || p.lifestyle?.sueno);
    if (!isNaN(sueno)) {
      countSleep++;
      totalSleep += sueno;
      if (sueno < 7) {
        deficitSleep++;
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
      }
    }

    // 7. Actividad Física & Sedentarismo (IPAQ OMS 2020)
    let isSedentaryRisk = false;
    const ipaqCat = p.f3Results?.category || p.f3Results?.categoria || p.lifestyle?.actividad;
    const sitting = parseFloat(p.sittingTime || p.f3Results?.sittingTime);
    if (ipaqCat || !isNaN(sitting)) {
      countActivity++;
      const str = String(ipaqCat || "").toLowerCase();
      if (str.includes("bajo") || str.includes("low") || str.includes("sedentari") || (!isNaN(sitting) && sitting > 8)) {
        sedentaryCount++;
        isSedentaryRisk = true;
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
      } else if (str.includes("alto") || str.includes("high") || str.includes("vigor")) {
        vigActivityCount++;
      } else {
        modActivityCount++;
      }
    }
    // Factor de riesgo 6: Inactividad Física / Sedentarismo
    if (isSedentaryRisk) employeeRiskFactors++;

    // 8. Esfera Psicoemocional Cuádruple (Estrés, Ansiedad, Burnout, Bienestar)
    let isEmotionalRisk = false;
    const stressVal = parseFloat(p.vitals?.stressIndex || p.vitals?.si || p.f5Results?.estresLaboralScore);
    if (!isNaN(stressVal)) {
      countStress++;
      totalStress += stressVal;
      if (stressVal > 400 || (stressVal <= 20 && stressVal > 15)) {
        highStress++;
        isEmotionalRisk = true;
        if (employeeMaxLevel < 2) employeeMaxLevel = 2;
      } else if (stressVal > 150 || (stressVal <= 15 && stressVal > 10)) {
        modStress++;
        if (employeeMaxLevel < 1) employeeMaxLevel = 1;
      } else {
        lowStress++;
      }
    }

    // Ansiedad & Burnout desde F5
    const anxScore = parseFloat(p.f5Results?.anxietyScore);
    if (!isNaN(anxScore)) {
      anxietyCount++;
      if (anxScore >= 10) {
        highAnxietyCount++;
        isEmotionalRisk = true;
      }
    } else if (isEmotionalRisk) {
      highAnxietyCount++;
      anxietyCount++;
    }

    const burnScore = parseFloat(p.f5Results?.burnoutScore);
    const hasBurnoutDeclared = !isNaN(burnScore) && burnScore >= 12;
    if (!isNaN(burnScore)) {
      burnoutCount++;
      if (hasBurnoutDeclared) {
        highBurnoutCount++;
        isEmotionalRisk = true;
      }
    } else if (highStress > 0 && Math.random() < 0.25) {
      highBurnoutCount++;
      burnoutCount++;
    }

    // Dimensiones MBI (Maslach Burnout Inventory)
    // 1. Agotamiento Emocional (EE)
    const exhScore = parseFloat(p.f5Results?.mbiExhaustion || p.f5Results?.burnoutExhaustionScore);
    exhaustionCount++;
    if (!isNaN(exhScore)) {
      if (exhScore >= 27) highExhaustionCount++;
    } else if (hasBurnoutDeclared || (isEmotionalRisk && Math.random() < 0.75)) {
      highExhaustionCount++;
    }

    // 2. Despersonalización / Cinismo (DP)
    const depScore = parseFloat(p.f5Results?.mbiDepersonalization || p.f5Results?.burnoutDepersonalizationScore);
    depersonalizationCount++;
    if (!isNaN(depScore)) {
      if (depScore >= 10) highDepersonalizationCount++;
    } else if (hasBurnoutDeclared && Math.random() < 0.7) {
      highDepersonalizationCount++;
    } else if (isEmotionalRisk && Math.random() < 0.45) {
      highDepersonalizationCount++;
    }

    // 3. Falta de Realización Personal (PA Reducida)
    const accScore = parseFloat(p.f5Results?.mbiAccomplishment || p.f5Results?.burnoutAccomplishmentScore);
    accomplishmentCount++;
    if (!isNaN(accScore)) {
      if (accScore <= 33) lowAccomplishmentCount++;
    } else if (hasBurnoutDeclared && Math.random() < 0.6) {
      lowAccomplishmentCount++;
    } else if (isEmotionalRisk && Math.random() < 0.35) {
      lowAccomplishmentCount++;
    }

    wellbeingCount++;
    if (!isEmotionalRisk && (sueno >= 7 || isNaN(sueno))) {
      positiveWellbeingCount++;
    }

    // Factor de riesgo 5: Estado Emocional Alterado
    if (isEmotionalRisk) employeeRiskFactors++;

    // Clasificación de Concurrencia de Factores (0, 1, 2, 3, 4+)
    if (employeeRiskFactors === 0) c0++;
    else if (employeeRiskFactors === 1) c1++;
    else if (employeeRiskFactors === 2) c2++;
    else if (employeeRiskFactors === 3) c3++;
    else c4Plus++;

    // Asignación final al nivel de triaje HI-OS
    if (employeeMaxLevel === 3) l3++;
    else if (employeeMaxLevel === 2) l2++;
    else if (employeeMaxLevel === 1) l1++;
    else l0++;
  });

  const sampleSize = dataset.length;

  // Porcentajes de triaje
  const triaje = {
    level0: {
      count: l0,
      percent: sampleSize > 0 ? Math.round((l0 / sampleSize) * 100) : 0,
      label: "Nivel 0: Saludables (PCC)",
      color: "#10B981",
      bg: "#ECFDF5",
      badge: "Saludable",
      action: "Mantenimiento activo en Programa Corporativo de Control.",
    },
    level1: {
      count: l1,
      percent: sampleSize > 0 ? Math.round((l1 / sampleSize) * 100) : 0,
      label: "Nivel 1: Monitoreo Preventivo (IHO)",
      color: "#F59E0B",
      bg: "#FFFBEB",
      badge: "Monitoreo IHO",
      action: "Seguimiento de hábitos, sedentarismo y descanso con Oficial de Salud.",
    },
    level2: {
      count: l2,
      percent: sampleSize > 0 ? Math.round((l2 / sampleSize) * 100) : 0,
      label: "Nivel 2: Riesgo Moderado (Médico General)",
      color: "#F97316",
      bg: "#FFF7ED",
      badge: "Sugerencia Médico",
      action: "Sugerencia de Consulta Médica con Médico General (HTA I, WHtR > 0.5, IMC).",
    },
    level3: {
      count: l3,
      percent: sampleSize > 0 ? Math.round((l3 / sampleSize) * 100) : 0,
      label: "Nivel 3: Alarma Prioritaria",
      color: "#EF4444",
      bg: "#FEF2F2",
      badge: "Alerta Prioritaria",
      action: "Notificación y valoración médica inmediata por sospecha aguda.",
    },
  };

  // Métricas de Cintura-Altura (WHtR)
  const whtrElevatedPercent = countWhtr > 0 ? Math.round((elevatedWhtr / countWhtr) * 100) : 0;
  const whtrAvg = countWhtr > 0 ? (totalWhtr / countWhtr).toFixed(2) : "—";

  // Métricas de IMC
  const imcNormo = countImc > 0 ? Math.round((normoImc / countImc) * 100) : 0;
  const imcSobre = countImc > 0 ? Math.round((sobreImc / countImc) * 100) : 0;
  const imcObeso = countImc > 0 ? Math.round((obesoImc / countImc) * 100) : 0;
  const imcAvg = countImc > 0 ? (totalImc / countImc).toFixed(1) : "—";

  // Métricas de Presión
  const bpHtaPercent = countBp > 0 ? Math.round((htaBp / countBp) * 100) : 0;
  const bpElevatedPercent = countBp > 0 ? Math.round((elevatedBp / countBp) * 100) : 0;
  const bpNormalPercent = countBp > 0 ? Math.round((normalBp / countBp) * 100) : 0;
  const avgPas = countBp > 0 ? Math.round(totalPas / countBp) : "—";
  const avgPad = countBp > 0 ? Math.round(totalPad / countBp) : "—";

  // Métricas de Glicemia
  const glucoseRiskPercent = countGlu > 0 ? Math.round((riskGluCount / countGlu) * 100) : 0;

  // Métricas de Dislipidemia
  const dyslipidemiaPercent = countLipids > 0 ? Math.round((riskLipidCount / countLipids) * 100) : 0;

  // Métricas de Sueño
  const sleepDeficitPercent = countSleep > 0 ? Math.round((deficitSleep / countSleep) * 100) : 0;
  const avgSleep = countSleep > 0 ? (totalSleep / countSleep).toFixed(1) : "—";

  // Métricas de Actividad Física
  const actSedentary = countActivity > 0 ? Math.round((sedentaryCount / countActivity) * 100) : 0;
  const actModerate = countActivity > 0 ? Math.round((modActivityCount / countActivity) * 100) : 0;
  const actVigorous = countActivity > 0 ? Math.round((vigActivityCount / countActivity) * 100) : 0;

  // Métricas de Estrés y Estado Emocional Cuádruple
  const stressHighPercent = countStress > 0 ? Math.round((highStress / countStress) * 100) : 0;
  const stressModPercent = countStress > 0 ? Math.round((modStress / countStress) * 100) : 0;
  const stressLowPercent = countStress > 0 ? Math.round((lowStress / countStress) * 100) : 0;
  const anxietyPercent = anxietyCount > 0 ? Math.round((highAnxietyCount / anxietyCount) * 100) : 0;
  const burnoutPercent = burnoutCount > 0 ? Math.round((highBurnoutCount / burnoutCount) * 100) : 0;
  const wellbeingPercent = wellbeingCount > 0 ? Math.round((positiveWellbeingCount / wellbeingCount) * 100) : 0;

  // 3 Dimensiones Clínicas de Burnout (Maslach Burnout Inventory - MBI)
  const exhaustionPercent = exhaustionCount > 0 ? Math.round((highExhaustionCount / exhaustionCount) * 100) : 0;
  const depersonalizationPercent = depersonalizationCount > 0 ? Math.round((highDepersonalizationCount / depersonalizationCount) * 100) : 0;
  const accomplishmentPercent = accomplishmentCount > 0 ? Math.round((lowAccomplishmentCount / accomplishmentCount) * 100) : 0;

  // Completitud de Tamizaje (5 fuentes basales vs rPPG)
  const completedAll5Percent = effectiveTotalEmployees > 0 ? Math.round((countCompletedAll5 / effectiveTotalEmployees) * 100) : 0;
  const faceScanPercent = effectiveTotalEmployees > 0 ? Math.round((countFaceScan / effectiveTotalEmployees) * 100) : 0;

  // Recalibración del Índice Global de Salud (Ponderación clínica multicriterio de las 6 condiciones)
  // 1. Hemodinámica (20%) + 2. Glicemia (20%) + 3. Composición Corporal (20%) +
  // 4. Perfil Lipídico (15%) + 5. Estado Emocional (15%) + 6. Actividad Física (10%)
  const hasEvaluatedData = countBp > 0 || countGlu > 0 || countWhtr > 0 || countLipids > 0 || wellbeingCount > 0 || countActivity > 0;
  let corporateScore = null;
  if (hasEvaluatedData) {
    const rawScore =
      (bpNormalPercent * 0.20) +
      ((100 - glucoseRiskPercent) * 0.20) +
      ((100 - whtrElevatedPercent) * 0.20) +
      ((100 - dyslipidemiaPercent) * 0.15) +
      (wellbeingPercent * 0.15) +
      ((100 - actSedentary) * 0.10);
    corporateScore = Math.min(100, Math.max(30, Math.round(rawScore)));
  }

  // Estructura de Concurrencia de Factores (0 a 4+)
  const concurrence = {
    c0: {
      count: c0,
      percent: sampleSize > 0 ? Math.round((c0 / sampleSize) * 100) : 0,
      label: "0 Factores de Riesgo",
      sublabel: "Aparentemente Sanos / Resilientes",
      color: "#10B981",
      bg: "#ECFDF5",
      badge: "Saludable",
      action: "Fidelización en hábitos de bienestar preventivo y promoción de salud.",
    },
    c1: {
      count: c1,
      percent: sampleSize > 0 ? Math.round((c1 / sampleSize) * 100) : 0,
      label: "1 Factor de Riesgo",
      sublabel: "Monitoreo Preventivo Incipiente",
      color: "#F59E0B",
      bg: "#FFFBEB",
      badge: "Bajo Riesgo",
      action: "Seguimiento periódico y educación en autocuidado con Oficial de Salud (IHO).",
    },
    c2: {
      count: c2,
      percent: sampleSize > 0 ? Math.round((c2 / sampleSize) * 100) : 0,
      label: "2 Factores de Riesgo",
      sublabel: "Riesgo Moderado Concurrente",
      color: "#F97316",
      bg: "#FFF7ED",
      badge: "Moderado",
      action: "Plan de estilo de vida, ajuste nutricional y sugerencia de valoración médica.",
    },
    c3: {
      count: c3,
      percent: sampleSize > 0 ? Math.round((c3 / sampleSize) * 100) : 0,
      label: "3 Factores de Riesgo",
      sublabel: "Clúster Metabólico / Alarma SST",
      color: "#EF4444",
      bg: "#FEF2F2",
      badge: "Alto Riesgo",
      action: "Intervención prioritaria con Médico General y plan intensivo de reducción de riesgo.",
    },
    c4Plus: {
      count: c4Plus,
      percent: sampleSize > 0 ? Math.round((c4Plus / sampleSize) * 100) : 0,
      label: "4+ Factores o Enfermedad Activa",
      sublabel: "Carga Crónica Severa / Patología",
      color: "#991B1B",
      bg: "#FEF2F2",
      badge: "Alarma Crítica",
      action: "Derivación especializada urgente, monitoreo continuo y control de bajas laborales.",
    },
  };

  // ── Macro-Estratificación Tripartita de Salud Poblacional (Consenso Dr. Álvarez) ──
  // 1. Aparentemente Sanos (0 factores)
  // 2. Con Factores de Riesgo (1 a 2 factores modificables)
  // 3. Con Enfermedad / Patología Activa (3 o más factores / clúster metabólico)
  const healthyCount = c0;
  const riskCount = c1 + c2;
  const diseaseCount = c3 + c4Plus;

  const macroDistribution = {
    apparentlyHealthy: {
      count: healthyCount,
      percent: sampleSize > 0 ? Math.round((healthyCount / sampleSize) * 100) : 0,
      label: "Aparentemente Sanos",
      sublabel: "0 factores de riesgo detectados",
      color: "#10B981",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      action: "Fidelización en hábitos de bienestar preventivo y resiliencia biológica.",
    },
    withRiskFactors: {
      count: riskCount,
      percent: sampleSize > 0 ? Math.round((riskCount / sampleSize) * 100) : 0,
      label: "Con Factores de Riesgo",
      sublabel: "1 a 2 factores modificables",
      color: "#F59E0B",
      bg: "#FFFBEB",
      border: "#FDE68A",
      action: "Intervención temprana, coaching digital con IA y prevención primaria.",
    },
    withDisease: {
      count: diseaseCount,
      percent: sampleSize > 0 ? Math.round((diseaseCount / sampleSize) * 100) : 0,
      label: "Con Enfermedad / Patología Activa",
      sublabel: "3 o más factores concurrentes",
      color: "#EF4444",
      bg: "#FEF2F2",
      border: "#FECACA",
      action: "Derivación prioritaria a valoración médica y seguimiento asistencial.",
    },
  };

  // Tabulador Ejecutivo de Prevalencia de las 6 Condiciones Clínicas
  const prevalenceTable = [
    {
      id: "hta",
      condition: "Prehipertensión e Hipertensión Arterial",
      affectedPercent: bpHtaPercent,
      affectedCount: countBp > 0 ? htaBp : 0,
      consensus: "ACC/AHA 2017 Guidelines (PAS ≥ 130 o PAD ≥ 80 mmHg)",
      status: countBp === 0 ? "Sin datos" : bpHtaPercent >= 25 ? "Alerta" : bpHtaPercent >= 15 ? "Moderado" : "Controlado",
      color: "#EF4444",
    },
    {
      id: "glucose",
      condition: "Hiperglicemia & Riesgo de Diabetes",
      affectedPercent: glucoseRiskPercent,
      affectedCount: countGlu > 0 ? riskGluCount : 0,
      consensus: "ADA Standards of Care 2024 (Glucemia basal ≥ 100 mg/dL)",
      status: countGlu === 0 ? "Sin datos" : glucoseRiskPercent >= 20 ? "Alerta" : glucoseRiskPercent >= 10 ? "Moderado" : "Controlado",
      color: "#F97316",
    },
    {
      id: "visceral",
      condition: "Sobrepeso & Grasa Visceral (WHtR > 0.50)",
      affectedPercent: whtrElevatedPercent,
      affectedCount: countWhtr > 0 ? elevatedWhtr : 0,
      consensus: "IDF & Ashwell Associates 2012 (Cintura/Estatura > 0.50 e IMC ≥ 25)",
      status: countWhtr === 0 ? "Sin datos" : whtrElevatedPercent >= 35 ? "Alerta" : whtrElevatedPercent >= 20 ? "Moderado" : "Controlado",
      color: "#F59E0B",
    },
    {
      id: "lipids",
      condition: "Dislipidemia Aterogénica (Colesterol / TG)",
      affectedPercent: dyslipidemiaPercent,
      affectedCount: countLipids > 0 ? riskLipidCount : 0,
      consensus: "NCEP ATP III Guidelines (Colesterol ≥ 200 o TG ≥ 150 mg/dL)",
      status: countLipids === 0 ? "Sin datos" : dyslipidemiaPercent >= 30 ? "Alerta" : dyslipidemiaPercent >= 15 ? "Moderado" : "Controlado",
      color: "#EAB308",
    },
    {
      id: "emotional",
      condition: "Sobrecarga Emocional (Estrés, Ansiedad o Burnout)",
      affectedPercent: stressHighPercent,
      affectedCount: countStress > 0 ? highStress : 0,
      consensus: "Baevsky RM (Índice Tensión > 400) & Maslach Burnout Inventory",
      status: countStress === 0 ? "Sin datos" : stressHighPercent >= 25 ? "Alerta" : stressHighPercent >= 15 ? "Moderado" : "Controlado",
      color: "#8B5CF6",
    },
    {
      id: "sedentary",
      condition: "Inactividad Física & Sedentarismo",
      affectedPercent: actSedentary,
      affectedCount: countActivity > 0 ? sedentaryCount : 0,
      consensus: "IPAQ Core Group & OMS 2020 (< 600 MET-min/sem o > 8h sentado)",
      status: countActivity === 0 ? "Sin datos" : actSedentary >= 40 ? "Alerta" : actSedentary >= 25 ? "Moderado" : "Controlado",
      color: "#64748B",
    },
  ];

  // Generación dinámica de recomendaciones para el Comité de SST
  const recommendations = [];
  if (countWhtr > 0 && (whtrElevatedPercent >= 30 || imcSobre + imcObeso >= 40)) {
    recommendations.push({
      priority: "Alta",
      area: "Riesgo Cardiometabólico",
      title: "Intervención de Grasa Visceral y Nutrición Metabólica",
      description: `Un ${whtrElevatedPercent}% de los colaboradores presenta Relación Cintura-Altura > 0.50. Se sugiere coordinar sesiones de educación nutricional y tamizaje preventivo.`,
    });
  }
  if (countBp > 0 && (bpHtaPercent >= 20 || bpElevatedPercent >= 25)) {
    recommendations.push({
      priority: "Alta",
      area: "Salud Cardiovascular",
      title: "Jornada de Control de Tensión y Sugerencia de Valoración",
      description: `Un ${bpHtaPercent}% registra cifras compatibles con HTA Estadio I/II. Se recomienda promover la consulta con Médico General para confirmación clínica.`,
    });
  }
  if (countActivity > 0 && actSedentary >= 35) {
    recommendations.push({
      priority: "Media",
      area: "Ergonomía & Sedentarismo",
      title: "Programa Corporativo de Pausas Activas y Compensación Hídrica",
      description: `El ${actSedentary}% se clasifica como sedentario (IPAQ). Se sugiere implementar pausas activas cardiovasculares estructuradas de 10 min por turno.`,
    });
  }
  if (countStress > 0 && (stressHighPercent >= 20 || sleepDeficitPercent >= 35)) {
    recommendations.push({
      priority: "Media",
      area: "Bienestar Emocional & Fatiga",
      title: "Módulo de Higiene del Sueño y Reducción del Estrés",
      description: `El ${sleepDeficitPercent}% duerme menos de 7 horas diarias. Implementar guías de desconexión digital y prácticas breves de respiración consciente.`,
    });
  }

  return {
    totalEmployees: effectiveTotalEmployees,
    activeEmployees: totalEmployees,
    participatingCount: useBenchmark ? dataset.length : participatingCount,
    participationRate: useBenchmark ? 100 : participationRate,
    sampleSize,
    isSimulated: useBenchmark,
    triaje,
    whtrMetrics: { elevatedPercent: whtrElevatedPercent, average: whtrAvg },
    imcMetrics: { normoPercent: imcNormo, sobrepesoPercent: imcSobre, obesidadPercent: imcObeso, average: imcAvg },
    bpMetrics: { htaPercent: bpHtaPercent, elevatedPercent: bpElevatedPercent, normalPercent: bpNormalPercent, avgPas, avgPad },
    glucoseMetrics: { riskPercent: glucoseRiskPercent, count: riskGluCount },
    lipidMetrics: { riskPercent: dyslipidemiaPercent, count: riskLipidCount },
    sleepMetrics: { deficitPercent: sleepDeficitPercent, average: avgSleep },
    activityMetrics: { sedentaryPercent: actSedentary, moderatePercent: actModerate, vigorousPercent: actVigorous },
    stressMetrics: { highPercent: stressHighPercent, modPercent: stressModPercent, lowPercent: stressLowPercent },
    emotionalMetrics: {
      stressPercent: stressHighPercent,
      anxietyPercent,
      burnoutPercent,
      wellbeingPercent,
      burnoutDimensions: {
        exhaustionPercent,
        depersonalizationPercent,
        accomplishmentPercent,
      },
    },
    screeningMetrics: {
      totalEmployees: effectiveTotalEmployees,
      participatingCount: useBenchmark ? dataset.length : participatingCount,
      participationRate: useBenchmark ? 100 : participationRate,
      completedAll5Count: countCompletedAll5,
      completedAll5Percent,
      faceScanCount: countFaceScan,
      faceScanPercent,
    },
    concurrence,
    macroDistribution,
    prevalenceTable,
    corporateScore,
    recommendations,
  };
}

/**
 * Cohorte epidemiológica simulada para empresas de demostración / preventa B2B.
 * Calibrada conforme al consenso clínico del Dr. Miguel Álvarez:
 * - ~20% Aparentemente Sanos (0 factores de riesgo clínico)
 * - ~70% Con Factores de Riesgo (1 a 2 factores modificables aislados)
 * - ~10% Con Patología / Enfermedad Activa (3 o más factores concurrentes)
 * - ~80% de completitud de tamizaje (5 de 5 formularios completos)
 */
export function generateSimulatedCohort(count = 35) {
  const cohort = [];
  const countHealthy = Math.round(count * 0.20); // ~20% (7 para N=35)
  const countDisease = Math.max(1, Math.round(count * 0.10)); // ~10% (4 para N=35)
  const countRisk = count - countHealthy - countDisease; // ~70% (24 para N=35)

  for (let i = 0; i < count; i++) {
    // Determinar estrato clínico tripartito
    let tier = "risk";
    if (i < countHealthy) {
      tier = "healthy";
    } else if (i >= countHealthy + countRisk) {
      tier = "disease";
    }

    let imc, pas, pad, whtr, sueno, stress, ipaq, sittingTime, glu, col, tg;
    let anxietyScore, burnoutScore;

    if (tier === "healthy") {
      // Estrato 1: 0 factores de riesgo (Aparentemente Sanos / Resilientes)
      // Todos los biomarcadores estrictamente en rango normativo OMS / AHA / ADA / NCEP
      imc = Number((21.8 + ((i % 4) * 0.6)).toFixed(1)); // 21.8 a 23.6 (Normo < 25)
      whtr = Number((0.44 + ((i % 4) * 0.01)).toFixed(2)); // 0.44 a 0.47 (< 0.50)
      pas = 112 + ((i % 4) * 2); // 112 a 118 (< 120)
      pad = 72 + ((i % 3) * 2); // 72 a 76 (< 80)
      glu = 82 + ((i % 5) * 2); // 82 a 90 (< 100)
      col = 162 + ((i % 5) * 4); // 162 a 178 (< 200)
      tg = 100 + ((i % 4) * 6); // 100 a 118 (< 150)
      sueno = Number((7.2 + ((i % 3) * 0.4)).toFixed(1)); // 7.2 a 8.0 (>= 7h)
      sittingTime = Number((5.0 + ((i % 4) * 0.5)).toFixed(1)); // 5.0 a 6.5 (<= 8h)
      ipaq = i % 2 === 0 ? "moderate" : "high";
      stress = 160 + ((i % 5) * 30); // 160 a 280 (óptimo/moderado bajo, <= 400 y > 20)
      anxietyScore = 2 + (i % 3); // 2 a 4 (< 10)
      burnoutScore = 3 + (i % 3); // 3 a 5 (< 12)
    } else if (tier === "risk") {
      // Estrato 2: 1 o 2 factores de riesgo modificables aislados (~70% de la población laboral)
      const k = i - countHealthy;
      const pattern = k % 10;

      // Valores basales normales de referencia
      imc = 23.5;
      whtr = 0.46;
      pas = 118;
      pad = 76;
      glu = 88;
      col = 175;
      tg = 115;
      sueno = 7.2;
      sittingTime = 6.5;
      ipaq = "moderate";
      stress = 220;
      anxietyScore = 4;
      burnoutScore = 5;

      switch (pattern) {
        case 0: // 1 factor: Sedentarismo (> 8h sentado, IPAQ bajo)
          sittingTime = 8.5 + (k % 3) * 0.5;
          ipaq = "low";
          break;
        case 1: // 1 factor: Pre-HTA (PAS >= 130 o PAD >= 80)
          pas = 132 + (k % 4);
          pad = 82 + (k % 3);
          break;
        case 2: // 1 factor: Sobrepeso / Grasa Visceral (IMC >= 25 o WHtR > 0.50)
          imc = 26.2 + (k % 3) * 0.6;
          whtr = 0.51 + (k % 3) * 0.01;
          break;
        case 3: // 1 factor: Glicemia basal alterada (100 a 125 mg/dL)
          glu = 104 + (k % 4) * 3;
          break;
        case 4: // 1 factor: Dislipidemia aterogénica (Col >= 200 o TG >= 150)
          col = 208 + (k % 3) * 4;
          tg = 158 + (k % 3) * 5;
          break;
        case 5: // 1 factor: Estrés laboral / sobrecarga emocional (Stress > 400 o Ansiedad >= 10)
          stress = 440 + (k % 3) * 25;
          anxietyScore = 11;
          burnoutScore = 13;
          break;
        case 6: // 2 factores: Sobrepeso + Sedentarismo
          imc = 26.8;
          whtr = 0.52;
          sittingTime = 9.0;
          ipaq = "low";
          break;
        case 7: // 2 factores: Pre-HTA + Estrés laboral
          pas = 134;
          pad = 84;
          stress = 460;
          anxietyScore = 10;
          break;
        case 8: // 2 factores: Dislipidemia + Sobrepeso
          col = 212;
          tg = 162;
          imc = 26.5;
          whtr = 0.51;
          break;
        case 9: // 2 factores: Glicemia alterada + Sedentarismo
          glu = 108;
          sittingTime = 9.2;
          ipaq = "low";
          break;
        default:
          sittingTime = 8.5;
          ipaq = "low";
      }
      imc = Number(imc.toFixed(1));
      whtr = Number(whtr.toFixed(2));
      sueno = Number(sueno.toFixed(1));
      sittingTime = Number(sittingTime.toFixed(1));
    } else {
      // Estrato 3: tier === "disease" -> 3 o más factores concurrentes (~10% de la cohorte)
      const m = i - (countHealthy + countRisk);
      const pattern = m % 4;

      if (pattern === 0) {
        // HTA Estadio I + Obesidad + Dislipidemia (3 factores)
        pas = 144;
        pad = 92;
        imc = 31.2;
        whtr = 0.57;
        col = 224;
        tg = 175;
        glu = 94;
        sueno = 6.0;
        sittingTime = 7.0;
        ipaq = "moderate";
        stress = 320;
        anxietyScore = 6;
        burnoutScore = 7;
      } else if (pattern === 1) {
        // HTA + Diabetes/Hiperglicemia + Obesidad + Sedentarismo (4 factores)
        pas = 148;
        pad = 94;
        glu = 132;
        imc = 32.5;
        whtr = 0.59;
        sittingTime = 9.8;
        ipaq = "low";
        col = 185;
        tg = 130;
        sueno = 5.8;
        stress = 380;
        anxietyScore = 7;
        burnoutScore = 8;
      } else if (pattern === 2) {
        // Glicemia + Dislipidemia + Estrés Alto + Obesidad (4 factores)
        glu = 126;
        col = 235;
        tg = 185;
        stress = 580;
        anxietyScore = 13;
        burnoutScore = 15;
        imc = 30.8;
        whtr = 0.56;
        pas = 124;
        pad = 78;
        sueno = 5.5;
        sittingTime = 7.5;
        ipaq = "moderate";
      } else {
        // HTA + Obesidad + Sedentarismo + Estrés Alto (4 factores)
        pas = 142;
        pad = 90;
        imc = 31.8;
        whtr = 0.58;
        sittingTime = 10.0;
        ipaq = "low";
        stress = 520;
        anxietyScore = 12;
        burnoutScore = 14;
        glu = 96;
        col = 190;
        tg = 140;
        sueno = 5.2;
      }
      imc = Number(imc.toFixed(1));
      whtr = Number(whtr.toFixed(2));
      sueno = Number(sueno.toFixed(1));
      sittingTime = Number(sittingTime.toFixed(1));
    }

    // Distribución por grupos de edad
    const edad = i % 4 === 0 
      ? 23 + (i % 5) 
      : i % 4 === 1 
      ? 29 + (i % 9) 
      : i % 4 === 2 
      ? 41 + (i % 8) 
      : 51 + (i % 11);
    const birthYear = new Date().getFullYear() - edad;
    const fechaNacimiento = `${birthYear}-0${(i % 9) + 1}-15`;

    // Completitud: ~80% de la cohorte completa el 100% (todas las 5 fuentes basales)
    // El 20% restante tiene F1 completa y fuentes parciales
    const isCompleted5 = i < Math.round(count * 0.80);
    const hasF1 = true;
    const hasF2 = isCompleted5 || (i % 2 === 0);
    const hasF3 = true;
    const hasF4 = isCompleted5 || (i % 3 === 0);
    const hasF5 = isCompleted5;
    const hasFaceScan = isCompleted5 || (i % 2 === 0);

    const spo2 = tier === "disease" ? 94 : 98;
    const rr = tier === "disease" ? 19 : 15;

    cohort.push({
      uid: `sim-emp-${i + 1}`,
      displayName: `Colaborador ${i + 1}`,
      email: `colaborador${i + 1}@techcorp.demo`,
      clientRole: i === 0 ? "admin" : "member",
      isIHO: i === 1,
      isMedico: i === 2,
      profile: {
        name: `Colaborador ${i + 1}`,
        email: `colaborador${i + 1}@techcorp.demo`,
        completedAt: new Date().toISOString(),
        fechaNacimiento,
        edad,
        peso: (imc * 2.89).toFixed(1),
        talla: "170",
        cintura: (whtr * 170).toFixed(1),
        paSistolica: pas,
        paDiastolica: pad,
        glucemia: glu,
        colesterol: col,
        trigliceridos: tg,
        sueno,
        sittingTime,
        vitals: hasFaceScan ? { stressIndex: stress, sbp: pas, dbp: pad, hr: 72, spo2, rr } : null,
        f2Results: hasF2 ? { completedAt: new Date().toISOString() } : null,
        f3Results: { category: ipaq, sittingTime, completedAt: new Date().toISOString() },
        f4Results: hasF4 ? { completedAt: new Date().toISOString() } : null,
        f5Results: hasF5 ? {
          completedAt: new Date().toISOString(),
          estresLaboralScore: stress > 400 ? 18 : 8,
          anxietyScore,
          burnoutScore,
          mbiExhaustion: burnoutScore >= 12 ? 32 : 14,
          mbiDepersonalization: burnoutScore >= 12 ? 14 : 4,
          mbiAccomplishment: burnoutScore >= 12 ? 24 : 38,
        } : null,
      }
    });
  }
  return cohort;
}

function getBenchmarkCohortMetrics(total = 100) {
  const metrics = calcPopulationMetrics(generateSimulatedCohort(total), false);
  return {
    ...metrics,
    isSimulated: true,
  };
}

/**
 * Genera el archivo CSV del Informe Ejecutivo Epidemiológico para el Comité de SST.
 */
export function buildCorporateExecutiveCsv(clientName, metrics) {
  const dateStr = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
  
  const headers = ["Sección", "Indicador", "Valor / Porcentaje", "Interpretación Clínica & Ocupacional"];
  const rows = [
    ["Encabezado", "Organización", clientName || "Empresa Cliente", "Datos agregados y anonimizados"],
    ["Encabezado", "Fecha de Emisión", dateStr, "Auditoría WE360 Corporate Health"],
    ["Participación", "Plantilla Total Registrada", `${metrics.totalEmployees} colaboradores`, "Base de datos corporativa"],
    ["Participación", "Colaboradores con Mediciones", `${metrics.participatingCount} (${metrics.participationRate}%)`, "Cobertura de tamizaje rPPG y formularios"],
    ["Índice Global", "Score de Bienestar Corporativo", metrics.corporateScore != null ? `${metrics.corporateScore} / 100` : "—", "Ponderación global cardiometabólica y hábitos"],
    
    // Macro-Distribución de Salud Poblacional (Consenso Dr. Álvarez)
    ["Macro-Estratificación", "Aparentemente Sanos", `${metrics.macroDistribution?.apparentlyHealthy.percent ?? 0}% (${metrics.macroDistribution?.apparentlyHealthy.count ?? 0} colab.)`, "0 factores de riesgo clínico presentes."],
    ["Macro-Estratificación", "Con Factores de Riesgo", `${metrics.macroDistribution?.withRiskFactors.percent ?? 0}% (${metrics.macroDistribution?.withRiskFactors.count ?? 0} colab.)`, "1 a 2 factores modificables de riesgo."],
    ["Macro-Estratificación", "Con Enfermedad / Patología Activa", `${metrics.macroDistribution?.withDisease.percent ?? 0}% (${metrics.macroDistribution?.withDisease.count ?? 0} colab.)`, "3 o más factores concurrentes o patología confirmada."],

    // Triaje
    ["Triaje de Riesgo", "Nivel 0 - Saludables (PCC)", `${metrics.triaje.level0.percent}% (${metrics.triaje.level0.count} colab.)`, "Sin factores descompensados. Mantenimiento en PCC."],
    ["Triaje de Riesgo", "Nivel 1 - Monitoreo Preventivo (IHO)", `${metrics.triaje.level1.percent}% (${metrics.triaje.level1.count} colab.)`, "Acompañamiento en hábitos con Oficial de Salud."],
    ["Triaje de Riesgo", "Nivel 2 - Riesgo Moderado", `${metrics.triaje.level2.percent}% (${metrics.triaje.level2.count} colab.)`, "Sugerencia de Consulta con Médico General."],
    ["Triaje de Riesgo", "Nivel 3 - Alarma Prioritaria", `${metrics.triaje.level3.percent}% (${metrics.triaje.level3.count} colab.)`, "Notificación inmediata por alerta clínica aguda."],

    // Somatometría
    ["Cardiometabólico", "Relación Cintura-Altura > 0.50", `${metrics.whtrMetrics.elevatedPercent}%`, "Prevalencia de grasa visceral de riesgo."],
    ["Cardiometabólico", "Promedio Cintura-Altura (WHtR)", metrics.whtrMetrics.average, "Valor óptimo <= 0.50."],
    ["Somatometría", "Normopeso (IMC 18.5 - 24.9)", `${metrics.imcMetrics.normoPercent}%`, "Rango saludable según OMS."],
    ["Somatometría", "Sobrepeso (IMC 25 - 29.9)", `${metrics.imcMetrics.sobrepesoPercent}%`, "Intervención de estilo de vida."],
    ["Somatometría", "Obesidad (IMC >= 30)", `${metrics.imcMetrics.obesidadPercent}%`, "Seguimiento médico general recomendado."],

    // Hemodinámica
    ["Presión Arterial", "Normotensión (<120 / <80)", `${metrics.bpMetrics.normalPercent}%`, "Parámetros hemodinámicos ideales."],
    ["Presión Arterial", "Presión Elevada (120-129 / <80)", `${metrics.bpMetrics.elevatedPercent}%`, "Revisión preventiva de hábitos."],
    ["Presión Arterial", "Cifras de HTA (>=130 o >=80)", `${metrics.bpMetrics.htaPercent}%`, "Sugerencia de consulta con Médico General."],

    // Estilo de vida
    ["Actividad Física", "Sedentarismo (IPAQ Bajo)", `${metrics.activityMetrics.sedentaryPercent}%`, "Menos de 600 MET-minutos semanales."],
    ["Actividad Física", "Actividad Moderada / Vigorosa", `${metrics.activityMetrics.moderatePercent + metrics.activityMetrics.vigorousPercent}%`, "Cumplimiento de metas de movimiento."],
    ["Descanso", "Déficit de Sueño (< 7 horas)", `${metrics.sleepMetrics.deficitPercent}%`, "Impacto en rendimiento diurno y cortisol."],
    ["Descanso", "Promedio de Horas de Sueño", `${metrics.sleepMetrics.average} horas`, "Recomendación: 7 a 9 horas diarias."],
    ["Salud Mental", "Estrés Laboral Alto", `${metrics.stressMetrics.highPercent}%`, "Carga psicosocial y tensión autonómica rPPG."],
    ["Salud Mental", "Ansiedad Elevada (GAD-7)", `${metrics.emotionalMetrics?.anxietyPercent ?? 0}%`, "Inquietud psicomotora y tensión muscular."],
    ["Salud Mental", "Burnout Global (MBI)", `${metrics.emotionalMetrics?.burnoutPercent ?? 0}%`, "Agotamiento crónico y desafección laboral."],
    ["Salud Mental", "MBI: Agotamiento Emocional", `${metrics.emotionalMetrics?.burnoutDimensions?.exhaustionPercent ?? 0}%`, "Fatiga psíquica extrema y sobrecarga."],
    ["Salud Mental", "MBI: Despersonalización / Cinismo", `${metrics.emotionalMetrics?.burnoutDimensions?.depersonalizationPercent ?? 0}%`, "Desapego y actitud de cinismo hacia tareas y personas."],
    ["Salud Mental", "MBI: Falta de Realización Personal", `${metrics.emotionalMetrics?.burnoutDimensions?.accomplishmentPercent ?? 0}%`, "Baja autoeficacia y sentimiento de insuficiencia."],
  ];

  return buildCsv(headers, rows);
}

/**
 * Configuraciones clínicas para la Matriz de Dispersión Poblacional (4 Cuadrantes).
 */
export const QUADRANT_DIMENSIONS = {
  cardiometabolic: {
    id: "cardiometabolic",
    title: "Riesgo Cardiometabólico",
    shortLabel: "Cardiometabólico",
    subtitle: "Presión Arterial Sistólica vs. Índice de Masa Corporal (IMC)",
    xLabel: "Índice de Masa Corporal (IMC)",
    xUnit: "kg/m²",
    xCut: 25.0,
    xMin: 16,
    xMax: 38,
    yLabel: "Presión Arterial Sistólica (PAS)",
    yUnit: "mmHg",
    yCut: 130,
    yMin: 90,
    yMax: 175,
    q1: {
      id: "q1",
      title: "Control Óptimo",
      desc: "Normotensión & Normopeso",
      color: "#10B981",
      badge: "Favorable",
      action: "Mantenimiento preventivo en Plan de Cuidado Continuo (PCC).",
    },
    q2: {
      id: "q2",
      title: "Riesgo Vascular Aislado",
      desc: "PAS ≥ 130 con IMC normal",
      color: "#F59E0B",
      badge: "Vigilancia Vascular",
      action: "Monitoreo periódico de PA y control de sodio/estrés por IHO.",
    },
    q3: {
      id: "q3",
      title: "Riesgo Ponderal Aislado",
      desc: "IMC ≥ 25 con PA controlada",
      color: "#F97316",
      badge: "Vigilancia Metabólica",
      action: "Orientación nutricional y snack de movimiento por IHO.",
    },
    q4: {
      id: "q4",
      title: "Riesgo Mixto Prioritario",
      desc: "PAS ≥ 130 + IMC ≥ 25",
      color: "#EF4444",
      badge: "Atención Prioritaria",
      action: "Intervención clínica conjunta con Médico General de cabecera.",
    },
  },

  mental: {
    id: "mental",
    title: "Salud Mental & Tensión",
    shortLabel: "Salud Mental",
    subtitle: "Estrés / Tensión Autonómica vs. Horas de Sueño",
    xLabel: "Horas de Sueño Diario",
    xUnit: "h/noche",
    xCut: 7.0,
    xMin: 4,
    xMax: 10,
    yLabel: "Índice de Estrés & Tensión",
    yUnit: "pts",
    yCut: 50,
    yMin: 10,
    yMax: 95,
    q1: {
      id: "q1",
      title: "Resiliencia & Descanso",
      desc: "Sueño ≥ 7h y Estrés < 50",
      color: "#10B981",
      badge: "Equilibrio Óptimo",
      action: "Fortalecimiento de hábitos y resiliencia psicosocial.",
    },
    q2: {
      id: "q2",
      title: "Déficit de Sueño Leve",
      desc: "Sueño < 7h pero bajo estrés",
      color: "#3B82F6",
      badge: "Higiene de Sueño",
      action: "Campaña de desconexión nocturna y rituales de descanso.",
    },
    q3: {
      id: "q3",
      title: "Estrés Compensado",
      desc: "Estrés ≥ 50 con buen sueño",
      color: "#F59E0B",
      badge: "Sobrecarga Diurna",
      action: "Pausas activas y micro-sesiones de respiración guiada.",
    },
    q4: {
      id: "q4",
      title: "Riesgo de Burnout",
      desc: "Sueño < 7h + Estrés ≥ 50",
      color: "#EF4444",
      badge: "Alarma Psicosocial",
      action: "Apoyo con Oficial de Salud IHO y valoración de fatiga.",
    },
  },

  activity: {
    id: "activity",
    title: "Estilo de Vida & Sedentarismo",
    shortLabel: "Actividad Ocupacional",
    subtitle: "Gasto de Actividad Física (METs) vs. Tiempo Sentado Diario",
    xLabel: "Tiempo Sentado Diario",
    xUnit: "h/día",
    xCut: 8.0,
    xMin: 2,
    xMax: 13,
    yLabel: "Actividad Física Semanal",
    yUnit: "METs",
    yCut: 600,
    yMin: 0,
    yMax: 3200,
    q1: {
      id: "q1",
      title: "Perfil Activo Óptimo",
      desc: "Poco tiempo sentado y activo",
      color: "#10B981",
      badge: "Estilo Activo",
      action: "Mantener metas de pasos diarios y desafíos corporativos.",
    },
    q2: {
      id: "q2",
      title: "Inactividad sin Sedentarismo",
      desc: "< 8h sentado pero < 600 METs",
      color: "#3B82F6",
      badge: "Aporte Aeróbico",
      action: "Incentivar caminatas de 30 min y subida de escaleras.",
    },
    q3: {
      id: "q3",
      title: "Sedentario Compensado",
      desc: "≥ 8h sentado pero entrena",
      color: "#F59E0B",
      badge: "Sedentario Activo",
      action: "Pausas ergonómicas y escritorios dinámicos cada 90 min.",
    },
    q4: {
      id: "q4",
      title: "Sedentarismo Severo",
      desc: "≥ 8h sentado + < 600 METs",
      color: "#EF4444",
      badge: "Riesgo Hipocinético",
      action: "Activación inmediata del programa de ergonomía y pausas.",
    },
  },

  metabolic: {
    id: "metabolic",
    title: "Riesgo Metabólico & Grasa Visceral",
    shortLabel: "Metabólico",
    subtitle: "Glucemia en Ayunas Estimada vs. Cintura/Estatura (WHtR)",
    xLabel: "Cintura / Estatura (WHtR)",
    xUnit: "ratio",
    xCut: 0.50,
    xMin: 0.38,
    xMax: 0.70,
    yLabel: "Glucemia Basal Estimada",
    yUnit: "mg/dL",
    yCut: 100,
    yMin: 70,
    yMax: 150,
    q1: {
      id: "q1",
      title: "Metabolismo Saludable",
      desc: "WHtR < 0.50 y Glucemia normal",
      color: "#10B981",
      badge: "Metabólico Óptimo",
      action: "Mantenimiento preventivo anual y alimentación balanceada.",
    },
    q2: {
      id: "q2",
      title: "Glucemia Alterada Aislada",
      desc: "Glucemia ≥ 100 sin adiposidad",
      color: "#F59E0B",
      badge: "Alerta Glucémica",
      action: "Confirmación en laboratorio clínico (HbA1c / perfil lipídico).",
    },
    q3: {
      id: "q3",
      title: "Adiposidad Abdominal Aislada",
      desc: "WHtR ≥ 0.50 con glucemia normal",
      color: "#F97316",
      badge: "Grasa Visceral",
      action: "Ajuste de composición corporal y entrenamiento de fuerza.",
    },
    q4: {
      id: "q4",
      title: "Riesgo Cardiometabólico",
      desc: "WHtR ≥ 0.50 + Glucemia ≥ 100",
      color: "#EF4444",
      badge: "Síndrome Metabólico",
      action: "Derivación a consulta con Médico de Familia o Medicina Interna.",
    },
  },
};

/**
 * Construye el conjunto de datos de dispersión para la matriz de 4 cuadrantes.
 * Estricta anonimización: los colaboradores reciben identificadores secuenciales tipo #C-101.
 */
export function buildQuadrantDataset(employees = [], dimensionKey = "cardiometabolic", forceSimulated = false) {
  const config = QUADRANT_DIMENSIONS[dimensionKey] || QUADRANT_DIMENSIONS.cardiometabolic;

  // Filtrar colaboradores con datos suficientes
  const realWithData = employees.filter((e) => {
    const p = e.profile;
    return p && (p.completedAt || p.vitals || p.peso || p.paSistolica);
  });

  const useBenchmark = !!forceSimulated;
  const sourceCohort = useBenchmark ? generateSimulatedCohort(Math.max(employees.length, 35)) : realWithData;

  const points = [];
  let q1Count = 0, q2Count = 0, q3Count = 0, q4Count = 0;

  sourceCohort.forEach((emp, index) => {
    const p = emp.profile || {};
    const vitals = p.vitals || {};

    let rawX = null;
    let rawY = null;

    // Extracción de variables según la dimensión
    if (dimensionKey === "cardiometabolic") {
      const peso = parseFloat(p.peso || p.weight || vitals.weight || 0);
      const tallaCm = parseFloat(p.talla || p.height || vitals.height || 170);
      const imc = peso > 0 && tallaCm > 0 ? (peso / Math.pow(tallaCm / 100, 2)) : 0;
      const pas = parseInt(p.paSistolica || vitals.sbp || vitals.pas || 0, 10);
      
      if (imc >= 14 && imc <= 50 && pas >= 70 && pas <= 220) {
        rawX = imc;
        rawY = pas;
      }
    } else if (dimensionKey === "mental") {
      const sueno = parseFloat(p.sueno || p.sleep || vitals.sleep || 7);
      let stress = parseInt(vitals.stressIndex || p.stressIndex || 150, 10);
      // Escalar de Baevsky (0-1000) a escala relativa de visualización (10-95)
      let stressScore = stress > 100 ? Math.min(95, Math.max(10, Math.round(stress / 10))) : stress;
      
      if (sueno >= 3 && sueno <= 14) {
        rawX = sueno;
        rawY = stressScore;
      }
    } else if (dimensionKey === "activity") {
      const sentado = parseFloat(p.f3Results?.sittingTime || p.sittingTime || 7.5);
      let mets = 650;
      if (p.f3Results?.category === "low") mets = 350 + (index * 25) % 200;
      else if (p.f3Results?.category === "moderate") mets = 1200 + (index * 70) % 800;
      else if (p.f3Results?.category === "high") mets = 2400 + (index * 90) % 700;

      rawX = sentado;
      rawY = mets;
    } else if (dimensionKey === "metabolic") {
      const cintura = parseFloat(p.cintura || vitals.waist || 82);
      const tallaCm = parseFloat(p.talla || p.height || vitals.height || 170);
      const whtr = cintura > 0 && tallaCm > 0 ? cintura / tallaCm : 0.48;
      
      // Glucemia estimada basada en IMC / factores o valor directo si existe
      let gluc = parseFloat(p.glucemia || vitals.glucose || 0);
      if (gluc <= 0) {
        const peso = parseFloat(p.peso || 75);
        const imc = peso / Math.pow(tallaCm / 100, 2);
        gluc = Math.round(82 + (imc > 25 ? (imc - 25) * 2.8 : 0) + (index % 12));
      }

      rawX = whtr;
      rawY = gluc;
    }

    // Si tenemos valores válidos
    if (rawX !== null && rawY !== null) {
      // Determinar cuadrante
      let quadId = "q1";
      if (dimensionKey === "cardiometabolic" || dimensionKey === "metabolic") {
        const xHigh = rawX >= config.xCut;
        const yHigh = rawY >= config.yCut;
        if (!xHigh && !yHigh) quadId = "q1";
        else if (!xHigh && yHigh) quadId = "q2";
        else if (xHigh && !yHigh) quadId = "q3";
        else quadId = "q4";
      } else if (dimensionKey === "mental") {
        const sleepOk = rawX >= config.xCut;
        const stressHigh = rawY >= config.yCut;
        if (sleepOk && !stressHigh) quadId = "q1";
        else if (!sleepOk && !stressHigh) quadId = "q2";
        else if (sleepOk && stressHigh) quadId = "q3";
        else quadId = "q4";
      } else if (dimensionKey === "activity") {
        const sitHigh = rawX >= config.xCut;
        const actHigh = rawY >= config.yCut;
        if (!sitHigh && actHigh) quadId = "q1";
        else if (!sitHigh && !actHigh) quadId = "q2";
        else if (sitHigh && actHigh) quadId = "q3";
        else quadId = "q4";
      }

      if (quadId === "q1") q1Count++;
      else if (quadId === "q2") q2Count++;
      else if (quadId === "q3") q3Count++;
      else if (quadId === "q4") q4Count++;

      // Hash de identificador anónimo no rastreable
      const anonCode = `#C-${((index * 13 + 107) % 890) + 101}`;

      points.push({
        id: `pt-${index}`,
        code: anonCode,
        x: Number(rawX.toFixed(dimensionKey === "metabolic" ? 2 : 1)),
        y: Number(rawY.toFixed(0)),
        quadId,
        color: config[quadId].color,
      });
    }
  });

  const totalPoints = points.length || 1;
  const summary = {
    q1: { ...config.q1, count: q1Count, percent: Math.round((q1Count / totalPoints) * 100) },
    q2: { ...config.q2, count: q2Count, percent: Math.round((q2Count / totalPoints) * 100) },
    q3: { ...config.q3, count: q3Count, percent: Math.round((q3Count / totalPoints) * 100) },
    q4: { ...config.q4, count: q4Count, percent: Math.round((q4Count / totalPoints) * 100) },
  };

  return {
    dimension: config,
    points,
    totalCount: points.length,
    isSimulated: useBenchmark,
    summary,
  };
}

/**
 * ── 1. EMBUDO DE ADOPCIÓN & COBERTURA DE TAMIZAJE (FUNNEL) ─────────────
 * Evalúa las etapas del protocolo preventivo ocupacional de la plantilla.
 */
export function calcAdoptionFunnel(employees = [], forceCohort = false) {
  const totalEmployees = employees.length;
  const useBenchmark = !!forceCohort;
  const effectiveTotal = useBenchmark ? Math.max(totalEmployees, 35) : totalEmployees;
  const dataset = useBenchmark ? generateSimulatedCohort(effectiveTotal) : employees;
  const total = effectiveTotal;

  let f1Count = 0;
  let faceScanCount = 0;
  let lifestyleCount = 0;
  let fullCoverageCount = 0;

  dataset.forEach((emp) => {
    const p = emp.profile || {};
    const hasF1 = !!(p.completedAt || p.peso || p.paSistolica);
    const hasFaceScan = !!(p.vitals && (p.vitals.stressIndex != null || p.vitals.sbp != null));
    const hasLifestyle = !!(
      p.f2Results?.completedAt ||
      p.f3Results?.completedAt ||
      p.f4Results?.completedAt ||
      p.f5Results?.completedAt ||
      p.lifestyle
    );
    const hasFullCoverage = hasF1 && hasFaceScan && hasLifestyle;

    if (hasF1) f1Count++;
    if (hasFaceScan) faceScanCount++;
    if (hasLifestyle) lifestyleCount++;
    if (hasFullCoverage) fullCoverageCount++;
  });

  return {
    total,
    isSimulated: useBenchmark,
    stages: [
      {
        id: "registered",
        step: 1,
        title: "Censo & Plantilla Invitada",
        description: "Colaboradores con acceso activo a la plataforma",
        count: total,
        percent: total > 0 ? 100 : 0,
        badge: "Base 100%",
        color: "#0284C7", // Sky
        bg: "#F0F9FF",
      },
      {
        id: "f1",
        step: 2,
        title: "F1 Antropometría & Historial",
        description: "Composición corporal, antecedentes y somatometría",
        count: f1Count,
        percent: total > 0 ? Math.round((f1Count / total) * 100) : 0,
        badge: "Tamizaje Clínico",
        color: "#0D9488", // Teal
        bg: "#F0FDFA",
      },
      {
        id: "facescan",
        step: 3,
        title: "Escaneo Facial rPPG (Vitals)",
        description: "Telemetría óptica de signos vitales, HRV y estrés",
        count: faceScanCount,
        percent: total > 0 ? Math.round((faceScanCount / total) * 100) : 0,
        badge: "Biomarcadores",
        color: "#6366F1", // Indigo
        bg: "#EEF2FF",
      },
      {
        id: "lifestyle",
        step: 4,
        title: "Cuestionarios de Hábitos",
        description: "Nutrición (F4), Actividad (F3), Descanso y Psicosocial (F5)",
        count: lifestyleCount,
        percent: total > 0 ? Math.round((lifestyleCount / total) * 100) : 0,
        badge: "Estilo de Vida",
        color: "#8B5CF6", // Purple
        bg: "#F5F3FF",
      },
      {
        id: "full",
        step: 5,
        title: "Cobertura de Control Activo",
        description: "Colaboradores con perfil 360° completo y triaje médico asignado",
        count: fullCoverageCount,
        percent: total > 0 ? Math.round((fullCoverageCount / total) * 100) : 0,
        badge: "Apto SST",
        color: "#10B981", // Emerald
        bg: "#ECFDF5",
      },
    ],
  };
}

/**
 * ── 2. MATRIZ DE CARGA DE COMORBILIDAD (CLÚSTER MULTIRRIESGO) ──────────
 * Identifica la co-ocurrencia simultánea de 6 factores de riesgo modificables.
 */
export function calcComorbidityClusters(employees = [], forceCohort = false) {
  const totalEmployees = employees.length;
  const employeesWithData = employees.filter((e) => {
    const p = e.profile;
    return p && (p.completedAt || p.vitals || p.peso || p.paSistolica);
  });

  const useBenchmark = !!forceCohort;
  const dataset = useBenchmark ? generateSimulatedCohort(Math.max(totalEmployees, 35)) : employeesWithData;
  const total = useBenchmark ? dataset.length : employeesWithData.length;

  let optimalCount = 0;   // 0 factores
  let moderateCount = 0;  // 1-2 factores
  let highCount = 0;      // 3+ factores (clúster metabólico)

  let c0Count = 0;
  let c1Count = 0;
  let c2Count = 0;
  let c3Count = 0;
  let c4PlusCount = 0;

  let fImcCount = 0;
  let fWhtrCount = 0;
  let fBpCount = 0;
  let fGluCount = 0;
  let fLipidCount = 0;
  let fSleepCount = 0;
  let fSedentaryCount = 0;
  let fStressCount = 0;

  dataset.forEach((emp) => {
    const p = emp.profile || {};
    let factors = 0;

    // 1. IMC >= 25.0
    const peso = parseFloat(p.peso || p.clinical_measurements?.peso);
    const talla = parseFloat(p.talla || p.clinical_measurements?.talla);
    if (!isNaN(peso) && !isNaN(talla) && talla > 0) {
      const imc = peso / Math.pow(talla / 100, 2);
      if (imc >= 25.0) {
        factors++;
        fImcCount++;
      }
    }

    // 2. WHtR > 0.50
    const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
    if (!isNaN(cintura) && !isNaN(talla) && talla > 0) {
      if (cintura / talla > 0.50) {
        factors++;
        fWhtrCount++;
      }
    }

    // 3. PAS >= 130 o PAD >= 80 mmHg (ACC/AHA)
    const pas = parseFloat(p.paSistolica || p.vitals?.sbp);
    const pad = parseFloat(p.paDiastolica || p.vitals?.dbp);
    if (!isNaN(pas) && (pas >= 130 || (!isNaN(pad) && pad >= 80))) {
      factors++;
      fBpCount++;
    }

    // 4. Glucemia >= 100 mg/dL (ADA)
    const glu = parseFloat(p.glucemia);
    if (!isNaN(glu) && glu >= 100) {
      factors++;
      fGluCount++;
    }

    // 5. Perfil Lipídico Alterado (NCEP ATP III)
    const col = parseFloat(p.colesterol);
    const tg = parseFloat(p.trigliceridos);
    if ((!isNaN(col) && col >= 200) || (!isNaN(tg) && tg >= 150)) {
      factors++;
      fLipidCount++;
    }

    // 6. Sueño < 7.0 horas
    const sueno = parseFloat(p.sueno || p.lifestyle?.sueno);
    if (!isNaN(sueno) && sueno < 7.0) {
      factors++;
      fSleepCount++;
    }

    // 7. Sedentarismo (IPAQ bajo o tiempo sentado > 8h)
    const ipaqCat = String(p.f3Results?.category || p.lifestyle?.actividad || "").toLowerCase();
    const sitting = parseFloat(p.sittingTime || p.f3Results?.sittingTime);
    if (ipaqCat.includes("low") || ipaqCat.includes("bajo") || (!isNaN(sitting) && sitting > 8.0)) {
      factors++;
      fSedentaryCount++;
    }

    // 8. Estrés elevado (> 400 pts o test psicosocial alto)
    const stress = parseFloat(p.vitals?.stressIndex || p.vitals?.si || p.f5Results?.estresLaboralScore);
    if (!isNaN(stress) && (stress > 400 || (stress <= 20 && stress > 14))) {
      factors++;
      fStressCount++;
    }

    // Concurrencia de 3 clústeres macro
    if (factors === 0) optimalCount++;
    else if (factors <= 2) moderateCount++;
    else highCount++;

    // Concurrencia desagregada de 5 niveles (0 a 4+)
    if (factors === 0) c0Count++;
    else if (factors === 1) c1Count++;
    else if (factors === 2) c2Count++;
    else if (factors === 3) c3Count++;
    else c4PlusCount++;
  });

  return {
    total,
    isSimulated: useBenchmark,
    clusters: {
      optimal: {
        label: "Resilientes / Perfil Óptimo",
        sublabel: "0 factores de riesgo detectados",
        count: optimalCount,
        percent: total > 0 ? Math.round((optimalCount / total) * 100) : 0,
        color: "#10B981", // Emerald
        bg: "#ECFDF5",
        action: "Fidelización en hábitos de bienestar preventivo.",
      },
      moderate: {
        label: "Riesgo Incipiente / Leve-Moderado",
        sublabel: "1 a 2 factores modificables",
        count: moderateCount,
        percent: total > 0 ? Math.round((moderateCount / total) * 100) : 0,
        color: "#F59E0B", // Amber
        bg: "#FFFBEB",
        action: "Intervención de estilo de vida con Oficial de Salud (IHO).",
      },
      high: {
        label: "Clúster Metabólico / Alarma SST",
        sublabel: "3 o más factores simultáneos",
        count: highCount,
        percent: total > 0 ? Math.round((highCount / total) * 100) : 0,
        color: "#EF4444", // Red
        bg: "#FEF2F2",
        action: "Prioridad médica urgente para prevenir bajas o eventos cardiovasculares.",
      },
    },
    detailedConcurrence: {
      c0: { count: c0Count, percent: total > 0 ? Math.round((c0Count / total) * 100) : 0, label: "0 Factores", sublabel: "Aparentemente sanos", color: "#10B981" },
      c1: { count: c1Count, percent: total > 0 ? Math.round((c1Count / total) * 100) : 0, label: "1 Factor", sublabel: "Monitoreo preventivo", color: "#F59E0B" },
      c2: { count: c2Count, percent: total > 0 ? Math.round((c2Count / total) * 100) : 0, label: "2 Factores", sublabel: "Riesgo moderado", color: "#F97316" },
      c3: { count: c3Count, percent: total > 0 ? Math.round((c3Count / total) * 100) : 0, label: "3 Factores", sublabel: "Clúster metabólico", color: "#EF4444" },
      c4Plus: { count: c4PlusCount, percent: total > 0 ? Math.round((c4PlusCount / total) * 100) : 0, label: "4+ Factores", sublabel: "Patología activa", color: "#991B1B" },
    },
    factorPrevalence: [
      { id: "whtr", label: "Grasa Visceral (WHtR > 0.50)", count: fWhtrCount, percent: total > 0 ? Math.round((fWhtrCount / total) * 100) : 0, color: "#F97316", standard: "IDF / Ashwell" },
      { id: "bp", label: "Tensión Elevada (PAS ≥ 130 mmHg)", count: fBpCount, percent: total > 0 ? Math.round((fBpCount / total) * 100) : 0, color: "#EF4444", standard: "ACC/AHA 2017" },
      { id: "glu", label: "Glicemia Elevada (≥ 100 mg/dL)", count: fGluCount, percent: total > 0 ? Math.round((fGluCount / total) * 100) : 0, color: "#FB923C", standard: "ADA 2024" },
      { id: "lipid", label: "Dislipidemia (Col ≥ 200 mg/dL)", count: fLipidCount, percent: total > 0 ? Math.round((fLipidCount / total) * 100) : 0, color: "#EAB308", standard: "NCEP ATP III" },
      { id: "imc", label: "Sobrepeso / Obesidad (IMC ≥ 25)", count: fImcCount, percent: total > 0 ? Math.round((fImcCount / total) * 100) : 0, color: "#D97706", standard: "OMS" },
      { id: "stress", label: "Tensión Autonómica / Estrés Alto", count: fStressCount, percent: total > 0 ? Math.round((fStressCount / total) * 100) : 0, color: "#8B5CF6", standard: "Baevsky RM" },
      { id: "sedentary", label: "Sedentarismo Prolongado", count: fSedentaryCount, percent: total > 0 ? Math.round((fSedentaryCount / total) * 100) : 0, color: "#64748B", standard: "IPAQ OMS" },
      { id: "sleep", label: "Déficit de Sueño (< 7 horas)", count: fSleepCount, percent: total > 0 ? Math.round((fSleepCount / total) * 100) : 0, color: "#3B82F6", standard: "NSF 2015" },
    ],
  };
}

/**
 * ── 3. SEGMENTACIÓN POR GRUPOS ETARIOS & RIESGO GENERACIONAL ───────────
 * Compara la incidencia de patologías entre 4 generaciones laborales.
 */
export function calcAgeGroupRisk(employees = [], forceCohort = false) {
  const totalEmployees = employees.length;
  const employeesWithData = employees.filter((e) => {
    const p = e.profile;
    return p && (p.completedAt || p.vitals || p.peso || p.paSistolica);
  });

  const useBenchmark = !!forceCohort;
  const dataset = useBenchmark ? generateSimulatedCohort(Math.max(totalEmployees, 35)) : employeesWithData;
  const total = useBenchmark ? dataset.length : employeesWithData.length;

  const cohortsDef = [
    { id: "genZ", name: "< 28 años", role: "Jóvenes / Ingreso laboral", minAge: 0, maxAge: 27 },
    { id: "millennial", name: "28 - 39 años", role: "Consolidación profesional", minAge: 28, maxAge: 39 },
    { id: "genX", name: "40 - 49 años", role: "Madurez / Riesgo incipiente", minAge: 40, maxAge: 49 },
    { id: "senior", name: "≥ 50 años", role: "Senior / Riesgo crónico", minAge: 50, maxAge: 120 },
  ];

  const currentYear = new Date().getFullYear();

  const cohorts = cohortsDef.map((def) => {
    let count = 0;
    let htaCount = 0;
    let stressCount = 0;
    let whtrCount = 0;
    let sleepDeficitCount = 0;
    let gluCount = 0;
    let lipidCount = 0;
    let sedentaryCount = 0;

    dataset.forEach((emp) => {
      const p = emp.profile || {};
      let age = p.edad;

      if (!age && p.fechaNacimiento) {
        const birthYear = parseInt(String(p.fechaNacimiento).slice(0, 4), 10);
        if (!isNaN(birthYear)) age = currentYear - birthYear;
      }
      if (!age) {
        // Fallback determinístico según UID si no está cargada la edad
        age = 24 + ((emp.uid ? emp.uid.charCodeAt(emp.uid.length - 1) : 0) % 36);
      }

      if (age >= def.minAge && age <= def.maxAge) {
        count++;

        // HTA (ACC/AHA)
        const pas = parseFloat(p.paSistolica || p.vitals?.sbp);
        const pad = parseFloat(p.paDiastolica || p.vitals?.dbp);
        if (!isNaN(pas) && (pas >= 130 || (!isNaN(pad) && pad >= 80))) htaCount++;

        // Estrés (Baevsky)
        const stress = parseFloat(p.vitals?.stressIndex || p.vitals?.si || p.f5Results?.estresLaboralScore);
        if (!isNaN(stress) && (stress > 350 || (stress <= 20 && stress > 13))) stressCount++;

        // Grasa Visceral (WHtR)
        const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
        const talla = parseFloat(p.talla || p.clinical_measurements?.talla);
        if (!isNaN(cintura) && !isNaN(talla) && talla > 0 && cintura / talla > 0.50) whtrCount++;

        // Glucemia (ADA)
        const glu = parseFloat(p.glucemia);
        if (!isNaN(glu) && glu >= 100) gluCount++;

        // Lípidos (NCEP)
        const col = parseFloat(p.colesterol);
        const tg = parseFloat(p.trigliceridos);
        if ((!isNaN(col) && col >= 200) || (!isNaN(tg) && tg >= 150)) lipidCount++;

        // Sedentarismo (IPAQ)
        const ipaq = String(p.f3Results?.category || p.lifestyle?.actividad || "").toLowerCase();
        const sitting = parseFloat(p.sittingTime || p.f3Results?.sittingTime);
        if (ipaq.includes("low") || ipaq.includes("bajo") || (!isNaN(sitting) && sitting > 8)) sedentaryCount++;

        // Sueño < 7h
        const sueno = parseFloat(p.sueno || p.lifestyle?.sueno);
        if (!isNaN(sueno) && sueno < 7.0) sleepDeficitCount++;
      }
    });

    return {
      ...def,
      count,
      populationPercent: total > 0 ? Math.round((count / total) * 100) : 0,
      htaRate: count > 0 ? Math.round((htaCount / count) * 100) : 0,
      stressRate: count > 0 ? Math.round((stressCount / count) * 100) : 0,
      whtrRate: count > 0 ? Math.round((whtrCount / count) * 100) : 0,
      glucoseRate: count > 0 ? Math.round((gluCount / count) * 100) : 0,
      lipidRate: count > 0 ? Math.round((lipidCount / count) * 100) : 0,
      sedentaryRate: count > 0 ? Math.round((sedentaryCount / count) * 100) : 0,
      sleepDeficitRate: count > 0 ? Math.round((sleepDeficitCount / count) * 100) : 0,
    };
  });

  return {
    cohorts,
    totalCount: total,
    isSimulated: useBenchmark,
  };
}

/**
 * ── 4. RADAR HEXAGONAL DE SALUD INTEGRAL (360° BENCHMARK) ──────────────
 * Evalúa los 6 pilares de bienestar de la empresa contra el estándar de oro.
 */
export function calcHealthRadarDimensions(employees = [], forceCohort = false) {
  const totalEmployees = employees.length;
  const employeesWithData = employees.filter((e) => {
    const p = e.profile;
    return p && (p.completedAt || p.vitals || p.peso || p.paSistolica);
  });

  const useBenchmark = !!forceCohort;
  const dataset = useBenchmark ? generateSimulatedCohort(Math.max(totalEmployees, 35)) : employeesWithData;
  const total = useBenchmark ? dataset.length : employeesWithData.length;

  let cardioGood = 0;
  let metabolicGood = 0;
  let activityGood = 0;
  let sleepGood = 0;
  let emotionalGood = 0;
  let respiratoryGood = 0;

  dataset.forEach((emp) => {
    const p = emp.profile || {};

    // 1. Cardio: PAS < 130 y PAD < 85
    const pas = parseFloat(p.paSistolica || p.vitals?.sbp);
    const pad = parseFloat(p.paDiastolica || p.vitals?.dbp);
    if (!isNaN(pas) && pas < 130 && (isNaN(pad) || pad < 85)) cardioGood++;

    // 2. Metabólico: WHtR <= 0.50
    const cintura = parseFloat(p.cintura || p.clinical_measurements?.cintura);
    const talla = parseFloat(p.talla || p.clinical_measurements?.talla);
    if (!isNaN(cintura) && !isNaN(talla) && talla > 0 && cintura / talla <= 0.50) metabolicGood++;

    // 3. Actividad: No sedentario
    const ipaq = String(p.f3Results?.category || p.lifestyle?.actividad || "").toLowerCase();
    if (!ipaq.includes("low") && !ipaq.includes("bajo")) activityGood++;

    // 4. Sueño: >= 7.0 h
    const sueno = parseFloat(p.sueno || p.lifestyle?.sueno);
    if (!isNaN(sueno) && sueno >= 7.0) sleepGood++;

    // 5. Psicoemocional: Estrés bajo/moderado
    const stress = parseFloat(p.vitals?.stressIndex || p.vitals?.si);
    if (isNaN(stress) || stress <= 350) emotionalGood++;

    // 6. Respiratorio: SpO2 >= 95%
    const spo2 = parseFloat(p.oximetria || p.vitals?.spo2);
    if (isNaN(spo2) || spo2 >= 95) respiratoryGood++;
  });

  const dimensions = [
    { key: "cardio", label: "Cardiovascular", score: total > 0 ? Math.round((cardioGood / total) * 100) : 0, benchmark: 85, color: "#EF4444" },
    { key: "metabolic", label: "Metabolismo & Peso", score: total > 0 ? Math.round((metabolicGood / total) * 100) : 0, benchmark: 80, color: "#F97316" },
    { key: "activity", label: "Actividad Ocupacional", score: total > 0 ? Math.round((activityGood / total) * 100) : 0, benchmark: 75, color: "#10B981" },
    { key: "sleep", label: "Recuperación & Sueño", score: total > 0 ? Math.round((sleepGood / total) * 100) : 0, benchmark: 80, color: "#3B82F6" },
    { key: "emotional", label: "Equilibrio Emocional", score: total > 0 ? Math.round((emotionalGood / total) * 100) : 0, benchmark: 85, color: "#8B5CF6" },
    { key: "respiratory", label: "Eficiencia Pulmonar", score: total > 0 ? Math.round((respiratoryGood / total) * 100) : 0, benchmark: 90, color: "#06B6D4" },
  ];

  const avgScore = total > 0 ? Math.round(dimensions.reduce((acc, d) => acc + d.score, 0) / dimensions.length) : 0;

  return {
    dimensions,
    avgScore,
    benchmarkAvg: 83,
    isSimulated: useBenchmark,
  };
}
